import React from 'react';
import {
	FormattedMessage,
	defineMessages,
} from 'react-intl';

import NoDataMessage from './NoDataMessage';
import NumberWithBar from '~/components/patterns/charts/structures/NumberWithBar';
import WebVitalFormatter from '~/components/app/WebVitalFormatter';
import WebVitalDistributionBarChart from '~/components/app/WebVitalDistributionBarChart';
import WidgetCell from '~/components/patterns/widgets/WidgetCell';
import Widget from './Widget';

import {
	useLargestContentfulPaintWidgetQuery,
} from './LargestContentfulPaintWidget.gql';

import useDashboardDateRange from '~/hooks/useDashboardDateRange';
import useDashboardQuery from '~/hooks/useDashboardQuery';
import useDashboardScopeSection from '~/hooks/useDashboardScopeSection';
import useWebsiteId from '~/hooks/useWebsiteId';

import {
	WidgetType,
	formatDateForQuery,
} from '~/model/dashboard';

import {
	WebVital,
} from '~/model/webVitals';



const messages = defineMessages({
	primaryActionLink: {
		id: 'ui.dashboardLargestContentfulPaintWidget.primaryActionLink',
	},
	title: {
		id: 'ui.dashboardLargestContentfulPaintWidget.title',
	},
	subtitle: {
		id: 'ui.dashboardLargestContentfulPaintWidget.subtitle',
	},
});



const LargestContentfulPaintWidget: React.FC = () => {
	const dateRange = useDashboardDateRange();
	const websiteId = useWebsiteId();
	const scopeSection = useDashboardScopeSection();

	const {
		data: metricData,
		loading,
		reasonForNoData,
	} = useDashboardQuery('webVitalsOriginSummaryData', useLargestContentfulPaintWidgetQuery, {
		variables: {
			scope: scopeSection.scope,
			websiteId,
			...formatDateForQuery(dateRange),
		},
	});

	return (
		<WidgetCell type={WidgetType.LargestContentfulPaint}>
			<Widget
				flag={(
					<FormattedMessage {...messages.subtitle} />
				)}
				label={(
					<FormattedMessage {...messages.title} />
				)}
				loading={loading}
				overlay={!loading && !metricData?.largestContentfulPaint && (
					<NoDataMessage reasonForNoData={reasonForNoData} />
				)}
				primaryActionLink={{
					label: (
						<FormattedMessage {...messages.primaryActionLink} />
					),
					routeName: 'website.platform',
					routeParams: {
						websiteId,
					},
				}}
				showEndDate={true}
			>
				{!loading && metricData?.largestContentfulPaint && (
					<NumberWithBar
						barChart={(
							<WebVitalDistributionBarChart
								maximumFast={metricData.largestContentfulPaint.maximumFast}
								maximumGood={metricData.largestContentfulPaint.maximumGood}
								percentageFast={metricData.largestContentfulPaint.percentageFast}
								percentageGood={metricData.largestContentfulPaint.percentageGood}
								percentageSlow={metricData.largestContentfulPaint.percentageSlow}
								type={WebVital.LCP}
							/>
						)}
						number={(
							<WebVitalFormatter
								showStatusIndicator={true}
								type={WebVital.LCP}
								value={metricData.largestContentfulPaint.percentile}
								verdict={metricData.largestContentfulPaint.verdict}
							/>
						)}
						stretchedBarChart={true}
					/>
				)}
			</Widget>
		</WidgetCell>
	);
};



export default LargestContentfulPaintWidget;
